/**
 * Wishlist storage manager.
 *
 * Stores wishlist items in localStorage so they survive page reloads and the
 * customer can curate a list as a guest. When customers sign in the data still
 * lives in the browser, so apparaten worden niet gesynced — dat is bewust een
 * keuze voor deze eerste iteratie (zie README).
 *
 * Schema (versie 1):
 *   localStorage[`mae:wishlist:v1`] = JSON.stringify([
 *     { id: '8123456789', handle: 'candy-oorringen', variantId: '4499...', addedAt: 1715600000000 },
 *     ...
 *   ])
 *
 * Wisseling van deze versie? Verhoog de versie in STORAGE_KEY en voeg een
 * migratie toe in `#migrate` zodat oude lijsten meeverhuizen.
 */
export class Wishlist {
  /** @type {string} localStorage key for the current schema version. */
  static STORAGE_KEY = 'mae:wishlist:v1';

  /** @type {string} Event dispatched on `window` whenever the wishlist changes. */
  static CHANGE_EVENT = 'wishlist:change';

  /** @type {number} Defensive upper bound to keep localStorage small. */
  static MAX_ITEMS = 100;

  /**
   * Returns all wishlist items, newest first.
   * @returns {{ id: string, handle: string, variantId?: string, addedAt: number }[]}
   */
  static getItems() {
    try {
      const raw = localStorage.getItem(Wishlist.STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  /**
   * Returns the number of items in the wishlist.
   * @returns {number}
   */
  static getCount() {
    return Wishlist.getItems().length;
  }

  /**
   * Checks whether a given product is in the wishlist.
   * @param {string | number} productId
   * @returns {boolean}
   */
  static has(productId) {
    if (productId == null) return false;
    const id = String(productId);
    return Wishlist.getItems().some((item) => item.id === id);
  }

  /**
   * Adds an item to the wishlist. No-op if it is already present.
   * @param {{ id: string | number, handle: string, variantId?: string | number }} payload
   * @returns {boolean} `true` if the item was added, `false` if it was already present.
   */
  static add({ id, handle, variantId } = {}) {
    if (id == null || !handle) return false;

    const items = Wishlist.getItems();
    const stringId = String(id);

    if (items.some((item) => item.id === stringId)) return false;

    const next = [
      { id: stringId, handle, variantId: variantId != null ? String(variantId) : undefined, addedAt: Date.now() },
      ...items,
    ].slice(0, Wishlist.MAX_ITEMS);

    Wishlist.#persist(next);
    Wishlist.#emit({ action: 'add', id: stringId, handle });
    return true;
  }

  /**
   * Removes an item from the wishlist.
   * @param {string | number} productId
   * @returns {boolean} `true` if the item was removed, `false` if it wasn't there.
   */
  static remove(productId) {
    if (productId == null) return false;
    const stringId = String(productId);
    const items = Wishlist.getItems();
    const next = items.filter((item) => item.id !== stringId);
    if (next.length === items.length) return false;

    Wishlist.#persist(next);
    Wishlist.#emit({ action: 'remove', id: stringId });
    return true;
  }

  /**
   * Toggles an item in the wishlist.
   * @param {{ id: string | number, handle: string, variantId?: string | number }} payload
   * @returns {'added' | 'removed' | 'noop'}
   */
  static toggle(payload) {
    if (!payload || payload.id == null) return 'noop';
    if (Wishlist.has(payload.id)) {
      return Wishlist.remove(payload.id) ? 'removed' : 'noop';
    }
    return Wishlist.add(payload) ? 'added' : 'noop';
  }

  /** Removes every item from the wishlist. */
  static clear() {
    localStorage.removeItem(Wishlist.STORAGE_KEY);
    Wishlist.#emit({ action: 'clear' });
  }

  /**
   * Persists the wishlist to localStorage.
   * @param {{ id: string, handle: string, variantId?: string, addedAt: number }[]} items
   */
  static #persist(items) {
    try {
      localStorage.setItem(Wishlist.STORAGE_KEY, JSON.stringify(items));
    } catch (_error) {
      // Quota exceeded or storage disabled — fail silently. The wishlist
      // becomes session-only at that point, which is acceptable.
    }
  }

  /**
   * Dispatches a wishlist change event on the window. Components hook into this
   * to update their UI without polling.
   * @param {{ action: 'add' | 'remove' | 'clear', id?: string, handle?: string }} detail
   */
  static #emit(detail) {
    window.dispatchEvent(
      new CustomEvent(Wishlist.CHANGE_EVENT, {
        detail: { ...detail, count: Wishlist.getCount() },
      })
    );
  }
}
